/**
 * Pure pricing-math helpers used by the Quote Calculator.
 * No React dependencies — safe to unit-test and reuse from other flows.
 */

const clampUnit = (u) => (u || 'inches').toLowerCase();

/** Convert a value in the given unit to inches. */
export const toInches = (v, unit) => {
  const u = clampUnit(unit);
  const n = parseFloat(v);
  if (!Number.isFinite(n)) return 0;
  if (u.startsWith('cm')) return n / 2.54;
  if (u.startsWith('mm')) return n / 25.4;
  return n;
};

/**
 * Normalise the calculator's width / height / unit into inches.
 * Returns { wIn, hIn, areaIn2 }. Falls back to 2×2 when either dimension
 * is missing so pricing doesn't divide by zero during early wizard steps.
 */
export const dimsInInches = (width, height, unit) => {
  const rawW = parseFloat(width) || 2;
  const rawH = parseFloat(height) || 2;
  const wIn = toInches(rawW, unit);
  const hIn = toInches(rawH, unit);
  return { wIn, hIn, areaIn2: wIn * hIn };
};

/**
 * Items that fit on a single 8.5"×11" sheet, given the sticker size in inches.
 * Pure function — no state reads.
 */
export const itemsPerPageFromInches = (wIn, hIn) => {
  const w = Math.max(0.01, wIn || 0.01);
  const h = Math.max(0.01, hIn || 0.01);
  const itemsAcross = Math.floor(8.5 / w);
  const itemsDown = Math.floor(11 / h);
  return Math.max(1, itemsAcross * itemsDown);
};

/** Version that accepts raw values in the current unit. */
export const calculateItemsPerPage = (w, h, unit) =>
  itemsPerPageFromInches(toInches(w, unit), toInches(h, unit));

/**
 * Size-based price multiplier, explicit inches.
 * Baseline 2"×2" (4 sq in) → 1.0×. sqrt curve, clamped to [0.6, 3.0].
 */
export const sizeMultiplierFromInches = (wIn, hIn) => {
  const area = (wIn || 0) * (hIn || 0);
  if (!area || area <= 0) return 1.0;
  const raw = Math.sqrt(area / 4);
  return Math.max(0.6, Math.min(3.0, raw));
};
